import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDollarSign } from 'react-icons/fa6';
import { toast } from 'sonner';

interface Product {
  productId: number;
  productTitle: string;
  image: string;
  price: number;
  discount: number;
  rating: number;
  popularity: number;
  description: string;
}

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const navigate = useNavigate();
  const discountedPrice = product.price - (product.price * product.discount / 100);
  const [isInCart, setIsInCart] = useState(false);

  useEffect(() => {
    // Load cart items on mount
    loadCartItems();
  }, []);

  const loadCartItems = () => {
    const cartJSON = localStorage.getItem('heritique-cart');
    const cart = cartJSON ? JSON.parse(cartJSON) : [];
    const isInCart = cart.some((item: any) => item.productId === product.productId);
    setIsInCart(isInCart);
  };

  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartItems();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const handleCardClick = () => {
    navigate(`/product/${product.productId}`);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Get current cart from localStorage
    const cartJSON = localStorage.getItem('heritique-cart');
    let cart = cartJSON ? JSON.parse(cartJSON) : [];
    
    if (isInCart) {
      // Remove from cart
      cart = cart.filter((item: any) => item.productId !== product.productId);
      localStorage.setItem('heritique-cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Removed from cart');
    } else {
      // Add to cart
      const existingItem = cart.find((item: any) => item.productId === product.productId);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        cart.push({ productId: product.productId, quantity: 1 });
      }
      localStorage.setItem('heritique-cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Added to cart successfully');
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer flex flex-col h-full"
    >
      <div className="relative">
        <img
          src={product.image}
          alt={product.productTitle}
          className="w-full h-48 object-cover"
        />
        {product.discount > 0 && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">
            -{product.discount}%
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 min-h-14">
          {product.productTitle}
        </h3>
        <div className="flex items-center mb-2">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(product.rating)
                    ? 'text-yellow-400'
                    : 'text-gray-300'
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
            <span className="ml-2 text-sm text-gray-600">
              {product.rating} ({product.popularity})
            </span>
          </div>
        </div>
        <div className="space-y-3 mt-auto">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold text-gray-800 flex items-center">
              <FaDollarSign className="mr-1" /> {(discountedPrice / 100).toFixed(2)}
            </span>
            {product.discount > 0 && (
              <span className="text-sm text-gray-500 line-through flex items-center">
                <FaDollarSign className="mr-1" /> {(product.price / 100).toFixed(2)}
              </span>
            )}
          </div>
          <button 
            onClick={handleButtonClick}
            className={`w-full px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              isInCart
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-[#f5ea1b] hover:bg-[#e6db0c] text-gray-900'
            }`}
          >
            {isInCart ? 'Remove from Cart' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;